import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AI_DENY_TARGETS, aiApi } from '@/lib/aiAdmin';
import { formatDateTime } from '@/lib/format';

import { CENTER, TITLE, columns, confirmedWrite, LoadFailed, Loading, useCursorList } from './common';

/** A deny rule blocks the models that match its pattern, for every organization. */
export function DenyRulesTab() {
  const list = useCursorList(async (token) => {
    const page = await aiApi.denyRules(token);
    return { rows: page.denyRules ?? [], next: page.nextPageToken };
  }, []);
  const [pattern, setPattern] = useState('');
  const [appliesTo, setAppliesTo] = useState('family');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!pattern.trim()) return;
    setBusy(true);
    const ok = await confirmedWrite(
      `Block ${appliesTo} "${pattern.trim()}" for every organization? It applies within about 30 seconds.`,
      () => aiApi.addDenyRule({ pattern: pattern.trim(), appliesTo }),
      'Deny rule added',
      'The rule was not added',
    );
    setBusy(false);
    if (ok) {
      setPattern('');
      list.reload();
    }
  };

  const remove = async (id: string, label: string) => {
    setBusy(true);
    const ok = await confirmedWrite(`Remove the deny rule "${label}"? Matching models can be called again within about 30 seconds.`, () => aiApi.deleteDenyRule(id), 'Deny rule removed', 'The rule was not removed');
    setBusy(false);
    if (ok) list.reload();
  };

  if (list.error) return <LoadFailed message={list.error} onRetry={list.reload} />;
  return (
    <DataTable
      toolbar={
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <Input value={pattern} onChange={(event) => setPattern(event.target.value)} placeholder="Pattern, such as gpt-*" aria-label="Pattern" className="h-9 w-56" />
          <Select value={appliesTo} onValueChange={setAppliesTo}>
            <SelectTrigger size="sm" className="h-9 w-32" aria-label="Applies to">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AI_DENY_TARGETS.map((target) => (
                <SelectItem key={target} value={target}>
                  {target}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit" size="sm" disabled={busy || !pattern.trim()}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Add deny rule
          </Button>
        </form>
      }
      footer={<TablePager {...list.pager} />}
    >
      <DataTableHead columns={columns(['Pattern', 'Applies to', 'Added by', 'Added', ''])} />
      <tbody>
        {list.rows.map((rule) => (
          <DataTableRow key={rule.id}>
            <DataTableCell className={TITLE}>{rule.pattern}</DataTableCell>
            <DataTableCell className={CENTER}>{rule.appliesTo}</DataTableCell>
            <DataTableCell className={CENTER}>{rule.createdBy || '—'}</DataTableCell>
            <DataTableCell className={CENTER}>{formatDateTime(rule.createdAt)}</DataTableCell>
            <DataTableCell className={CENTER}>
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void remove(rule.id, rule.pattern)}>
                <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Remove
              </Button>
            </DataTableCell>
          </DataTableRow>
        ))}
        {list.loading && list.rows.length === 0 ? (
          <tr>
            <td colSpan={5}>
              <Loading label="Loading deny rules" />
            </td>
          </tr>
        ) : null}
        {!list.loading && list.rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
              No deny rules.
            </td>
          </tr>
        ) : null}
      </tbody>
    </DataTable>
  );
}
